// Generated by tutorials/record.cjs from tutorials/articles/h40-menu-cycles.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h40 --text-only` (wording) or
// `node tutorials/record.cjs h40` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'menu-cycles',
  catalogId: 'h40',
  title: 'Your menu cycles',
  summary: 'The Menu Cycles page lists your facility\'s menu cycles and the holiday and one-time menus under each. Add a cycle, copy one to start next season\'s, choose which one is active, and get back one you deleted.',
  audience: 'Menu planners and administrators who set up the facility\'s menus',
  area: 'menus',
  learn: [
    'Tell which menu cycle is active',
    'See a cycle\'s holiday and one-time menus',
    'Add a new menu cycle',
    'Copy a cycle to start next season\'s',
    'Change a cycle\'s name, length or start date',
    'Make a cycle active',
    'Delete a cycle and get it back from the Recycle Bin',
  ],
  keywords: [
    'menu cycle',
    'menu cycles',
    'cycle',
    'rotation',
    'season',
    'active menu',
    'set active',
    'new menu',
    'copy menu',
    'start date',
    'length',
    'weeks',
    'holiday menu',
    'one time menu',
    'recycle bin',
    'restore',
    'delete menu',
  ],
  steps: [
    {
      id: 'open',
      part: 'Your cycles',
      heading: 'Open Menu Cycles',
      text: 'In the menu, select Menus, then Menu Cycles. Each row is a menu cycle, with its start date and length.\n\nThe check under Active marks the cycle your facility is serving now. Tray tickets, production and the selective menu all use it.',
      media: {
        image: {
          src: '/help/menu-cycles/open.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Menu Cycles page listing Fall/Winter Cycle 2025, Fall/Winter Cycle 2026 with the Active check, and Spring/Summer Cycle 2026, each 4 weeks long.',
      },
    },
    {
      id: 'find',
      part: 'Your cycles',
      heading: 'Find a cycle',
      text: 'Type part of a name in Find. The list narrows as you type. Clear Find to see every cycle again.',
      media: {
        image: {
          src: '/help/menu-cycles/find.webp',
          width: 1280,
          height: 854,
        },
        clip: {
          src: '/help/menu-cycles/find.mp4',
          width: 1280,
          height: 854,
          durationSec: 3.6,
        },
        alt: 'Find holds the word spring, and the list shows only Spring/Summer Cycle 2026.',
      },
    },
    {
      id: 'holidays',
      part: 'Your cycles',
      heading: 'See a cycle\'s holiday and one-time menus',
      text: 'Select the arrow at the left of a cycle. Its holiday and one-time menus are listed under it: Active shows the ones still to come, History the ones that have passed, and All every one.\n\nDate is the next day the menu is served, and Menu Day is where that date falls in the cycle, such as Week 4, Day 5. A menu with the repeat arrow comes back every time the cycle reaches its day.',
      media: {
        image: {
          src: '/help/menu-cycles/holidays.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/menu-cycles/holidays.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.2,
        },
        alt: 'Fall/Winter Cycle 2026 opened, listing New Year\'s Day Brunch, Christmas Day, Thanksgiving Dinner, Fish Friday Lunch, Christmas Eve Supper and Halloween Treats with their dates, menu days and mealtimes.',
      },
    },
    {
      id: 'new',
      part: 'Add or copy a cycle',
      heading: 'Add a new menu cycle',
      text: 'Select New. Type a name (names are kept in capitals), set the length in days with the arrows or by typing, and pick the start date: the date of day 1.\n\nA new cycle has no menu items yet. When you are starting a new season, copying last season\'s cycle (next step) is usually quicker.',
      media: {
        image: {
          src: '/help/menu-cycles/new.webp',
          width: 1280,
          height: 736,
        },
        clip: {
          src: '/help/menu-cycles/new.mp4',
          width: 1280,
          height: 736,
          durationSec: 5.6,
        },
        alt: 'The Add a New Menu Cycle window with Name SHORT STAY CYCLE, Length 14 days (2 weeks) and a start date.',
      },
    },
    {
      id: 'new-saved',
      part: 'Add or copy a cycle',
      heading: 'Save the new cycle',
      text: 'Select Save. The cycle is added to the list. It is not active, so nothing changes for the kitchen until you fill in its menus and make it active.',
      media: {
        image: {
          src: '/help/menu-cycles/new-saved.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/menu-cycles/new-saved.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.4,
        },
        alt: 'SHORT STAY CYCLE added to the Menu Cycles list, 2 weeks long, without the Active check.',
      },
    },
    {
      id: 'copy',
      part: 'Add or copy a cycle',
      heading: 'Copy a cycle to start next season\'s',
      text: 'Select the cycle to copy, then select More actions (the three dots) and Copy. Under Copy To, pick New for Where and type the new cycle\'s name.\n\nTick One Time and Recurring to bring the cycle\'s holiday and one-time menus along too. With Active Menus Only checked, only the ones selected for use come along.',
      media: {
        image: {
          src: '/help/menu-cycles/copy.webp',
          width: 1280,
          height: 844,
        },
        clip: {
          src: '/help/menu-cycles/copy.mp4',
          width: 1280,
          height: 844,
          durationSec: 6,
        },
        alt: 'The Menu Cycle Copy window copying Fall/Winter Cycle 2026: Where set to New, Menu Name Fall/Winter Cycle 2027, and One Time and Recurring checked.',
      },
    },
    {
      id: 'copied',
      part: 'Add or copy a cycle',
      heading: 'Make the copy',
      text: 'Check the summary at the bottom of the window, then select Copy. The new cycle is listed with every day, meal and diet rule of the one you copied.',
      media: {
        image: {
          src: '/help/menu-cycles/copied.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/menu-cycles/copied.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.4,
        },
        alt: 'Fall/Winter Cycle 2027 added to the Menu Cycles list, 4 weeks long, with its own holiday menus.',
      },
    },
    {
      id: 'details',
      part: 'Change a cycle',
      heading: 'Change a cycle\'s details',
      text: 'Select the cycle, then Open (or double-click it). On General Information, change the name, the length or the start date, then select Save.\n\nHere the copy starts on the first Sunday of next September, so day 1 of the new season falls on that date.',
      media: {
        image: {
          src: '/help/menu-cycles/details.webp',
          width: 1280,
          height: 736,
        },
        clip: {
          src: '/help/menu-cycles/details.mp4',
          width: 1280,
          height: 736,
          durationSec: 3.7,
        },
        alt: 'The Modify Menu Cycle window for Fall/Winter Cycle 2027 on General Information, with its name in capitals, with Length 28 days and the start date changed to 9/5/2027.',
      },
    },
    {
      id: 'set-active',
      part: 'Change a cycle',
      heading: 'Make a cycle active',
      text: 'When the new season starts, select its cycle, then More actions and Set Active. The check under Active moves to it, and from then on the kitchen serves from it.\n\nOnly one cycle is active at a time, so the cycle that was active before is not active any more.',
      media: {
        image: {
          src: '/help/menu-cycles/set-active.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/menu-cycles/set-active.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.7,
        },
        alt: 'The Active check now on FALL/WINTER CYCLE 2027, and a message saying it is now active and Fall/Winter Cycle 2026 is no longer active.',
      },
    },
    {
      id: 'delete',
      part: 'Delete and get a cycle back',
      heading: 'Delete a cycle',
      text: 'Select the cycle, then More actions and Delete Menu Cycle. Select Yes to move it, with its holiday menus, to the Recycle Bin.',
      media: {
        image: {
          src: '/help/menu-cycles/delete.webp',
          width: 708,
          height: 350,
        },
        clip: {
          src: '/help/menu-cycles/delete.mp4',
          width: 708,
          height: 350,
          durationSec: 2,
        },
        alt: 'The Delete Menu window asking whether to move SHORT STAY CYCLE and all associated holiday menus to the recycle bin, with No and Yes.',
      },
    },
    {
      id: 'recycle-bin',
      part: 'Delete and get a cycle back',
      heading: 'Open Deleted menus',
      text: 'Select Deleted menus on the toolbar. The number beside it is how many deleted cycles and holiday menus it holds.\n\nAuto Delete On is the day each one is deleted for good, 8 days after it was deleted.',
      media: {
        image: {
          src: '/help/menu-cycles/recycle-bin.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/menu-cycles/recycle-bin.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.8,
        },
        alt: 'The Recycle Bin listing SHORT STAY CYCLE and Fall/Winter Cycle 2026 - Copy with their start dates, lengths and Auto Delete On dates.',
      },
    },
    {
      id: 'restore',
      part: 'Delete and get a cycle back',
      heading: 'Get a cycle back',
      text: 'Select the cycle, then Restore. It goes back on the Menu Cycles page, with its holiday menus, just as it was.',
      media: {
        image: {
          src: '/help/menu-cycles/restore.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/menu-cycles/restore.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.3,
        },
        alt: 'The Recycle Bin after restoring SHORT STAY CYCLE: only Fall/Winter Cycle 2026 - Copy is left, and a message says SHORT STAY CYCLE was restored.',
      },
    },
    {
      id: 'modify',
      part: 'Work on a cycle\'s menus',
      heading: 'Open a cycle\'s menus',
      text: 'Back on Menu Cycles, select a cycle, then Modify. Modify opens the cycle\'s days, meals and items; see Reading a menu cycle.',
      media: {
        image: {
          src: '/help/menu-cycles/modify.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Menu Cycles page with Fall/Winter Cycle 2026 selected and the Modify button pointed at.',
      },
    },
  ],
  tips: [
    'Copy last season\'s cycle instead of starting from nothing. You keep every day, meal and diet rule, and change only what is new.',
    'Set up and check next season\'s cycle while the current one is still active. Nothing changes for the kitchen until you select Set Active.',
    'Double-click a cycle to open its details, the same as Open.',
    'Find narrows the list as you type. Clear it to see every cycle again.',
  ],
  notes: [
    'Only one cycle is active at a time. Tray tickets, production and the selective menu all follow the active cycle.',
    'The start date and the length set which cycle day falls on each date. Day 1 is the start date; after the last day the cycle starts again at day 1.',
    'A deleted cycle stays in the Recycle Bin for 8 days, with its holiday menus, and is then deleted for good.',
    'Adding, copying and deleting cycles need the right role. If a button is missing, ask your administrator.',
  ],
  related: [
    'reading-a-menu-cycle',
    'holiday-and-one-time-menus',
    'changing-menu-items',
    'menu-analysis',
    'importing-a-menu',
  ],
  recorded: '2026-10-26',
};

export default article;
